import { Layout, Network } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import ReactFlow, {
  Background,
  Connection,
  Controls,
  Edge,
  MarkerType,
  OnConnect,
  Panel,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ConfirmationModal } from '../common';

import { useOrgChart, useOrgChartContext } from '../../hooks/useOrgChart';
import { useAuth } from '../../store/hooks/useAuth';
import { useHasPermission } from '../../store/hooks/useRbac';
import { PERMISSIONS } from '../../utils/rbac/permissions';

import { OrgChartNodeData } from '../../types/org-chart';
import { getDagreLayout, LayoutConfig, wouldCreateCycle } from '../../utils/org-chart-utils';
import DetailsPanel from './DetailsPanel';
import EmployeeNode from './EmployeeNode';
import OrgToolbar from './OrgToolbar';
import { OrgChartSkeleton } from './Skeleton';

const nodeTypes = {
  employeeNode: EmployeeNode,
  containerNode: ({ data }: any) => (
    <div className="px-4 py-2 bg-gray-100/50 border-2 border-dashed border-gray-200 rounded-xl text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center min-w-[200px]">
      {data.label}
    </div>
  ),
};

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: false,
  style: { strokeWidth: 2.5, zIndex: 50 },
  pathOptions: { borderRadius: 30 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 14,
    height: 14,
  },
};

interface OrgChartProps {
  initialFocusedUserId?: string | null;
  hideToolbar?: boolean;
}

const OrgChartVisualizationInner = ({ initialFocusedUserId, hideToolbar }: OrgChartProps) => {
  const { nodes: globalNodes, loading: globalLoading, reassign, isReassigning } = useOrgChart();
  const { nodes: contextNodes, loading: contextLoading } = useOrgChartContext(initialFocusedUserId || '');
  const canManage = useHasPermission(PERMISSIONS.ORG_CHART_MANAGE) || useHasPermission(PERMISSIONS.EMPLOYEE_MANAGEMENT_MANAGE);
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';

  const apiNodes = initialFocusedUserId ? contextNodes : globalNodes;
  const loading = initialFocusedUserId ? contextLoading : globalLoading;
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { setViewport, fitView, zoomIn, zoomOut } = useReactFlow();

  const [selectedNode, setSelectedNode] = useState<OrgChartNodeData | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [edgeSourceNode, setEdgeSourceNode] = useState<OrgChartNodeData | null>(null);
  const [edgeTargetNode, setEdgeTargetNode] = useState<OrgChartNodeData | null>(null);
  const [pendingConnection, setPendingConnection] = useState<{ sourceId: string; targetId: string } | null>(null);
  const [removalModal, setRemovalModal] = useState<{ isOpen: boolean; targetId: string; type: 'reporting' | 'functional' } | null>(null);
  const [focusedUserId, setFocusedUserId] = useState<string | null>(initialFocusedUserId || null);
  const [role, setRole] = useState<'admin' | 'manager' | 'employee'>('admin');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchResults, setSearchResults] = useState<OrgChartNodeData[]>([]);
  const [layoutType, setLayoutType] = useState<'compact' | 'balanced' | 'spacious'>('compact');

  const layoutConfig: LayoutConfig = useMemo(() => {
    switch (layoutType) {
      case 'balanced':
        return { ranker: 'longest-path', nodesep: 80, ranksep: 160 };
      case 'spacious':
        return { ranker: 'network-simplex', nodesep: 120, ranksep: 200 };
      default:
        return { ranker: 'tight-tree', nodesep: 40, ranksep: 140 };
    }
  }, [layoutType]);

  // Use api data
  const rawNodes = useMemo(() => apiNodes, [apiNodes]);

  // Focus Logic: Calculate visible hierarchy using Dagre for consistency
  const calculateFocusLayout = useCallback((centerUserId: string) => {
    const centerUser = rawNodes.find(n => n.id === centerUserId);
    if (!centerUser) return;

    const managers = rawNodes.filter(n => n.id === centerUser.reportingManagerId || n.id === centerUser.functionalManagerId);
    const reports = rawNodes.filter(n => n.reportingManagerId === centerUserId || n.functionalManagerId === centerUserId);

    const relevantNodesData = [centerUser, ...managers, ...reports];
    const uniqueNodesData = Array.from(new Map(relevantNodesData.map(n => [n.id, n])).values());

    const { nodes: layoutedNodes, edges: layoutedEdges } = getDagreLayout(uniqueNodesData, 'TB', layoutConfig);

    const styledNodes = layoutedNodes.map(node => ({
      ...node,
      data: { ...node.data, isEditMode }
    }));

    const nodesMap = new Map(rawNodes.map(n => [n.id, n]));
    const styledEdges = layoutedEdges.map(edge => {
      const report = nodesMap.get(edge.target);
      const isFunctional = report?.functionalManagerId === edge.source && report?.reportingManagerId !== edge.source;
      const isBoth = report?.reportingManagerId === edge.source && report?.functionalManagerId === edge.source;

      let color = '#3B82F6';
      if (isBoth) color = '#EF4444';
      else if (isFunctional) color = '#10B981';

      return {
        ...edge,
        type: 'smoothstep',
        animated: isEditMode,
        style: {
          stroke: color,
          strokeWidth: isBoth ? 3 : 2.5,
          strokeDasharray: isEditMode ? '5,5' : undefined,
          zIndex: 50
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 14,
          height: 14,
          color: color
        }
      };
    });

    setNodes(styledNodes);
    setEdges(styledEdges);
    setTimeout(() => fitView({ padding: 0.3, duration: 800 }), 100);
  }, [rawNodes, setNodes, setEdges, fitView, isEditMode, layoutConfig]);

  // Initialize nodes and edges
  useEffect(() => {
    if (rawNodes.length > 0) {
      if (focusedUserId) {
        calculateFocusLayout(focusedUserId);
      } else {
        const { nodes: layoutedNodes, edges: layoutedEdges } = getDagreLayout(rawNodes, 'TB', layoutConfig);
        const styledNodes = layoutedNodes.map(node => ({
          ...node,
          data: { ...node.data, isEditMode }
        }));

        const nodesMap = new Map(rawNodes.map(n => [n.id, n]));
        const styledEdges = layoutedEdges.map(edge => {
          const report = nodesMap.get(edge.target);
          const isFunctional = report?.functionalManagerId === edge.source && report?.reportingManagerId !== edge.source;
          const isBoth = report?.reportingManagerId === edge.source && report?.functionalManagerId === edge.source;

          let color = '#3B82F6';
          if (isBoth) color = '#EF4444';
          else if (isFunctional) color = '#10B981';

          return {
            ...edge,
            type: 'smoothstep',
            animated: isEditMode,
            style: {
              stroke: color,
              strokeWidth: isBoth ? 3 : 2.5,
              strokeDasharray: isEditMode ? '5,5' : undefined,
              zIndex: 50
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 14,
              height: 14,
              color: color
            }
          };
        });

        setNodes(styledNodes);
        setEdges(styledEdges);
        setTimeout(() => fitView({ padding: 0.2 }), 100);
      }
    }
  }, [rawNodes, focusedUserId, calculateFocusLayout, setNodes, setEdges, fitView, isEditMode, layoutConfig]);

  const handleSearch = useCallback((term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    const results = rawNodes.filter((n) =>
      n?.fullName?.toLowerCase()?.includes(term.toLowerCase()) ||
      n?.employeeId?.toLowerCase()?.includes(term.toLowerCase())
    );
    setSearchResults(results.slice(0, 8));
  }, [rawNodes]);

  const handleSelectEmployee = useCallback((userId: string) => {
    setFocusedUserId(userId);
    setSearchResults([]);

    const user = rawNodes.find(n => n.id === userId);
    if (user) setSelectedNode(user);
  }, [rawNodes]);

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      if (role !== 'admin') {
        toast.error('Only admins can reassign employees');
        return;
      }
      const sourceId = params.source!;
      const targetId = params.target!;

      if (wouldCreateCycle(rawNodes, sourceId, targetId)) {
        toast.error('Cannot create circular reporting relationship');
        return;
      }

      setPendingConnection({ sourceId, targetId });
      setSelectedNode(null);
      setSelectedEdge(null);

      const source = rawNodes.find(n => n.id === sourceId);
      const target = rawNodes.find(n => n.id === targetId);
      setEdgeSourceNode(source || null);
      setEdgeTargetNode(target || null);
    },
    [role, rawNodes]
  );

  const reportingChain = useMemo(() => {
    if (!selectedNode) return [];

    // Create a lookup map for O(1) retrieval
    const nodesMap = new Map(rawNodes.map(n => [n.id, n]));
    const chain: OrgChartNodeData[] = [];
    const visited = new Set<string>();
    let current = nodesMap.get(selectedNode.reportingManagerId || '');
    let depth = 0;

    while (current && !visited.has(current.id) && depth < 100) {
      visited.add(current.id);
      chain.push(current);
      current = nodesMap.get(current.reportingManagerId || '');
      depth++;
    }
    return chain;
  }, [selectedNode, rawNodes]);

  const directReports = useMemo(() => {
    if (!selectedNode) return [];
    return rawNodes.filter(n => n.reportingManagerId === selectedNode.id || n.functionalManagerId === selectedNode.id);
  }, [selectedNode, rawNodes]);

  const toggleFullScreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const chartContent = (
    <div className="h-full w-full relative bg-white overflow-hidden">
      {loading && (
        <div className="absolute inset-0 bg-white z-[60]">
          <OrgChartSkeleton />
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        onNodeClick={(_, node) => {
          if (node.type === 'employeeNode') {
            setSelectedNode(node.data);
            setSelectedEdge(null);
            setFocusedUserId(node.id);
          }
        }}
        onEdgeClick={(_, edge) => {
          setSelectedEdge(edge);
          setSelectedNode(null);
          const source = rawNodes.find(n => n.id === edge.source);
          const target = rawNodes.find(n => n.id === edge.target);
          setEdgeSourceNode(source || null);
          setEdgeTargetNode(target || null);
        }}
        onPaneClick={() => {
          setSelectedNode(null);
          setSelectedEdge(null);
          setFocusedUserId(null);
        }}
        nodesDraggable={isEditMode}
        nodesConnectable={isEditMode}
        elementsSelectable={true}
        fitView
        minZoom={0.2}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#F8FAFC" gap={20} />
        <Controls showInteractive={false} className="!hidden" />

        {!hideToolbar && (
          <OrgToolbar
            onZoomIn={() => zoomIn()}
            onZoomOut={() => zoomOut()}
            onFitView={() => fitView({ padding: 0.2, duration: 800 })}
            onSearch={handleSearch}
            searchResults={searchResults}
            onSelectUser={handleSelectEmployee}
            isEditMode={isEditMode}
            onToggleEditMode={() => setIsEditMode(!isEditMode)}
            onToggleFullScreen={() => setIsFullscreen(!isFullscreen)}
            isFullscreen={isFullscreen}
            canManage={canManage}
          />
        )}

        <Panel position="bottom-left" className="flex flex-col items-start gap-3 mb-6 ml-6">
          {focusedUserId && (
            <button
              onClick={() => {
                setFocusedUserId(null);
                setSelectedNode(null);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-md border border-primary-100 rounded-2xl shadow-premium text-primary-600 text-[10px] font-bold uppercase tracking-widest hover:bg-primary-50 transition-all active:scale-95 group"
            >
              <Network size={14} className="group-hover:rotate-180 transition-transform duration-500" />
              Reset Global View
            </button>
          )}

          <div className="bg-white/80 backdrop-blur p-2.5 rounded-2xl border border-white shadow-premium">
            <div className="flex items-center gap-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-[3px] bg-blue-500 rounded-full" /> Reporting
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-[3px] bg-emerald-500 border-b border-dashed border-emerald-500" /> Functional
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-[3px] bg-red-500 rounded-full" /> Both
              </div>
            </div>
          </div>
        </Panel>

        <Panel position="bottom-right" className="mb-6 mr-6 flex flex-col items-end gap-3">
          <div className="bg-white/80 backdrop-blur p-1 rounded-2xl border border-white shadow-premium flex items-center gap-2">
            <div className="pl-3 pr-1 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-r border-slate-100 mr-1 flex items-center gap-2">
              <Layout size={12} />
              Layout
            </div>
            <div className="flex p-1 bg-slate-50 rounded-xl gap-1">
              {(['compact', 'balanced', 'spacious'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setLayoutType(type)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${layoutType === type
                      ? 'bg-white text-primary-600 shadow-sm ring-1 ring-slate-200'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                    }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </Panel>

        {apiNodes.length === 0 && !loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[2px] z-10 pointer-events-none">
            <div className="flex flex-col items-center gap-2 p-8 bg-white/90 rounded-3xl shadow-premium border border-white max-w-sm text-center">
              <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mb-2">
                <Network className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">No Hierarchy Data</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                We couldn't find any organizational structure data. Try refreshing or check if employees have managers assigned.
              </p>
            </div>
          </div>
        )}
      </ReactFlow>

      <DetailsPanel
        selectedNode={selectedNode}
        selectedEdge={selectedEdge}
        sourceNode={edgeSourceNode}
        targetNode={edgeTargetNode}
        isEditMode={isEditMode}
        onClose={() => {
          setSelectedNode(null);
          setSelectedEdge(null);
          setPendingConnection(null);
        }}
        reportingChain={reportingChain}
        directReports={directReports}
        reportingManager={rawNodes.find(n => n.id === selectedNode?.reportingManagerId) || null}
        functionalManager={rawNodes.find(n => n.id === selectedNode?.functionalManagerId) || null}
        onSelectNode={(id) => {
          setFocusedUserId(id);
          const user = rawNodes.find(n => n.id === id);
          if (user) setSelectedNode(user);
          setSelectedEdge(null);
          setPendingConnection(null);
        }}
        onFocusNode={(id) => {
          setFocusedUserId(id);
          const user = rawNodes.find(n => n.id === id);
          if (user) setSelectedNode(user);
          
          // Smooth zoom to focus
          setTimeout(() => {
            fitView({ 
              nodes: [{ id }], 
              duration: 800, 
              padding: 1.5 
            });
          }, 50);
        }}
        onRemoveEdge={(targetId, type) => {
          setRemovalModal({ isOpen: true, targetId, type });
        }}
        pendingConnection={pendingConnection}
        onConfirmAssignment={async (type) => {
          if (pendingConnection) {
            try {
              await reassign(pendingConnection.targetId, pendingConnection.sourceId, type);
              setPendingConnection(null);
              
              // Custom toast based on role
              if (isAdmin) {
                toast.success('Hierarchy updated successfully');
              } else {
                toast.success('Change request sent for approval');
              }
            } catch (err) {
              // Error handled by hook toast
            }
          }
        }}
        isReassigning={isReassigning}
      />

      {removalModal && (
        <ConfirmationModal
          isOpen={removalModal.isOpen}
          onClose={() => setRemovalModal(null)}
          onConfirm={async () => {
            try {
              await reassign(removalModal.targetId, null, removalModal.type);
              
              // Custom toast based on role
              if (isAdmin) {
                toast.success('Hierarchy link removed successfully');
              } else {
                toast.success('Removal request sent for approval');
              }
              
              setRemovalModal(null);
              setSelectedEdge(null);
            } catch (err) {
              // Error handled by hook toast
            }
          }}
          isLoading={isReassigning}
          title="Remove Manager Link"
          message={`Are you sure you want to remove the ${removalModal.type} manager link for this employee? ${isAdmin ? 'This will update the hierarchy immediately.' : 'This will trigger a workflow approval process.'}`}
          type="danger"
          confirmText="Remove Link"
        />
      )}
    </div>
  );

  return (
    <div className={
      isFullscreen
        ? "fixed inset-0 z-[100] bg-white flex flex-col"
        : "h-full w-full relative bg-white overflow-hidden"
    }>
      {chartContent}
    </div>
  );
};

const OrgChartVisualization = (props: OrgChartProps) => (
  <ReactFlowProvider>
    <OrgChartVisualizationInner {...props} />
  </ReactFlowProvider>
);

export default OrgChartVisualization;
